import type { ProjectWithInterviewCount } from "@repo/app-types/entities/project";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { type FC, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  clearFocusedInterview,
  fetchFocusedInterview,
  selectFocusedInterviewAndContact,
} from "../interviewsSlice.js";
import { InterviewSlideover } from "./interviewSlideover.js";

interface InterviewSlideoverContainerProps {
  project: ProjectWithInterviewCount;
}

export const InterviewSlideoverContainer: FC<
  InterviewSlideoverContainerProps
> = (props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // project_id is always in the URL but interview_id can be route param (/interview/abc) or query param (?interview_id=abc)
  const { interviewId: interviewIdRouteParam, projectId: projectId } =
    useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const interviewIdQueryParam = queryParams.get("interview_id");
  const interviewId = interviewIdRouteParam || interviewIdQueryParam;

  const focusedInterviewAndContact = useAppSelector(
    selectFocusedInterviewAndContact,
  );
  const { interview, contact } = focusedInterviewAndContact || {};

  useEffect(() => {
    if (interviewId && projectId)
      dispatch(
        fetchFocusedInterview({
          projectId,
          interviewId,
        }),
      );
    else if (!interviewId) dispatch(clearFocusedInterview());
  }, [interviewId, projectId]);

  const onClickClose = () => {
    dispatch(clearFocusedInterview());

    // Update the URL by removing interviewId query or route param
    if (interviewIdQueryParam) {
      // Insights tab case: url like /project/abc/insights?=interview_id
      navigate(
        // Remove the interview_id query param from the current location
        {
          search: "",
        },
        {
          replace: true,
        },
      );
    } else if (projectId) {
      // Interviews tab case: URL like: /project/abc/interviews/def
      navigate(
        buildProjectUrlPath(projectId, ProjectDetailsTabPath.Interviews),
      );
    }
  };

  return (
    <InterviewSlideover
      projectId={props.project.id}
      interviewId={interviewId ?? undefined}
      interview={interview}
      contact={contact}
      onClose={onClickClose}
    />
  );
};
